import { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import BookingFilter from "./BookingFilter";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import styled from "styled-components";
import DeleteBooking from "../DeleteBooking";
import MyBookingMultiDeleteDialog from "../booking-instruments/my-bookings/my-booking-list-view/my-booking-multi-delete-button/MyBookingMultiDeleteDialog";
import BookingNotificationStatus from "../booking-calendars/booking-notification-status/BookingNotificationStatus";
import { timeoutCallbackFactory } from "../booking-calendars/booking-notification-status/timeoutCallbackFactory";
import {
  GRID_VIEW,
  INVALID_FILTER,
  LIST_VIEW,
  MY_LAB,
  DEFAULT_COLUMNS,
  DEFAULT_FILTER,
  allTableColumn,
  EQUIPMENT_BASED_DEFAULT_SHOW_COLUMNS, TIMELINE_VIEW
} from "../../constants";
import { filter, find, sortBy } from "lodash";
import { useBookingOperations, useBookingSubscriptionStatus, BOOKING_OPERATIONS } from "../booking-operations";
import { quickEditTracker } from "../booking-operations/quickEditTracker";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { useSelector } from "react-redux";
import {
  convertStrToObjColumns,
  convertToCameCase,
  get4WeekStartAndEndInISOString,
} from "../../utils/helpers/text";
import BookingGrid from "./BookingGrid";
import EquipmentBookingGrid from "./EquipmentBookingGrid";
import { useParams } from "react-router-dom";
import {
  BOOKING_STORAGE_KEYS,
  GROUP_BOOKING_STORAGE_KEYS,
  getPersistedFilter,
  setPersistedFilter,
} from "../../utils/store/bookingFilterPersistence";
import { SharedFilterProvider } from "./SharedFilterContext";
import { generateViewKey, resetViewState } from "./ViewSwitchingUtils";
import { withApollo } from "react-apollo";
import BookingList from "./BookingList";
import EquipmentBookingList from "./EquipmentBookingList";
import { getAllMasterFilterData } from "../equipment-list/LoadEquipmentService";
import { getColumnObj } from "../equipment-list/EquipmentHelper";
import useTvMode from "../booking-instruments/useTvMode";
const autoGroupColField = "equipment_model";

const MainViewStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${props => (props.$isTvMode ? '0' : '8px')};
  padding-bottom: ${props => (props.$isTvMode ? '0' : '8px')};
  width: 100%;
  flex-grow: 1;
`;

function Bookings({
  subscriptionStatus,
  setSubscriptionStatus,
  setPopupEnableStatus,
  client,
  groupName,
  isGroupView = false
}) {
  const { trackPageView } = useMatomo();
  const { EventID } = useParams();
  const [eventID, setEventID] = useState(null);

  useEffect(() => {
    trackPageView({
      documentTitle: "Bookings",
      href: `${window.location?.origin}/Bookings`,
    });
    if (EventID) {
      setEventID(EventID);
      setView(GRID_VIEW);
    }
    // eslint-disable-next-line
  }, []);
  const [rowEquipmentId, setRowEquipmentId] = useState(null);
  const [shouldFetchData, triggerFetch] = useState(false);
  const [enableResetAll, setEnableResetAll] = useState(false);

  const user = useSelector((store) => store.user);
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);
  const [tvRefreshKey, setTvRefreshKey] = useState(0);

  const [resetAll, setResetAll] = useState(false);
  const storageKeys = isGroupView ? GROUP_BOOKING_STORAGE_KEYS : BOOKING_STORAGE_KEYS;
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(() => {
    return getPersistedFilter(storageKeys.SHOW_ADVANCE_FILTER, false);
  });
  const [disableResetAll, setDisableResetAll] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => {
    return getPersistedFilter(storageKeys.SEARCH_QUERY, "");
  });
  const [selectedEquipment, setSelectedEquipment] = useState([]);

  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const defaultDateRange = [
      get4WeekStartAndEndInISOString("start", user.timezone),
      get4WeekStartAndEndInISOString("end", user.timezone),
    ];
    return getPersistedFilter(storageKeys.SELECTED_DATE_RANGE, defaultDateRange);
  });

  const [tempRowData, setTempRowData] = useState([]);
  const [isDropBooking, setIsDropBooking] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openMultiDeleteDialog, setOpenMultiDeleteDialog] = useState(false);
  const { dispatchAction } = useContext(PaginationContext);
  const [status, setStatus] = useState(false);

  // Ref for calendar view to call methods imperatively
  const calendarRef = useRef(null);

  // States for hazardous/assistance approval workflow
  const [openHazardousRejectDialog, setOpenHazardousRejectDialog] = useState(false);
  const [hazardousBookingToReject, setHazardousBookingToReject] = useState(null);
  const [openAssistanceDialog, setOpenAssistanceDialog] = useState(false);
  const [bookingForAssistance, setBookingForAssistance] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);


  const [view, setView] = useState(() => {
    return getPersistedFilter(storageKeys.VIEW, GRID_VIEW);
  });
  const [isEquipmentView, setIsEquipmentView] = useState(() => {
    return getPersistedFilter(storageKeys.IS_EQUIPMENT_VIEW, false);
  });

  // Refs to track current view state for subscription callbacks (must be after state declarations)
  const currentViewRef = useRef(view);
  const isEquipmentViewRef = useRef(isEquipmentView);
  const [displayPreset, setDisplayPreset] = useState(() => {
    return getPersistedFilter(storageKeys.DISPLAY_PRESET, null);
  });
  const [listGroupByView, setListGroupByView] = useState(() => {
    return getPersistedFilter(storageKeys.LIST_GROUP_BY_VIEW, 'BOOKING');
  });
  const [savedFilter, setSavedFilter] = useState(() => {
    return getPersistedFilter(storageKeys.SAVED_FILTER, MY_LAB);
  });
  const [signeeFilter, setSigneeFilter] = useState(() => {
    return getPersistedFilter(storageKeys.SIGNEE_FILTER, "");
  });
  const [showFilters, setShowFilters] = useState(() => {
    return getPersistedFilter(storageKeys.SHOW_FILTERS, false);
  });

  const isTimelineView = view === TIMELINE_VIEW;
  const [showInvalidFilter, setShowInvalidFilter] = useState(false);
  const [showSideBar, setShowSideBar] = useState(false);
  const [currentSideBarPanel, setCurrentSideBarPanel] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isColumnActive, setIsColumnActive] = useState(false);

  const getDefaultEquipmentBasedColumns = () => {
    if (listGroupByView !== 'NONE') return DEFAULT_COLUMNS
    const defaultCols = structuredClone(DEFAULT_COLUMNS)
    defaultCols.displayDefinition.showColumns = EQUIPMENT_BASED_DEFAULT_SHOW_COLUMNS
    return defaultCols
  }
  const [dataModel, setDataModel] = useState([]);
  const [myLabFilter, setMyLabFilter] = useState(null);
  const [masterDataFilters, setMasterDataFilters] = useState({});
  const [userDisplayColumnMaster, setUserDisplayColumnMaster] = useState([]);
  const [userFilters, setUserFilters] = useState([]);
  const [lastFavFilter, setLastFavFilter] = useState(DEFAULT_FILTER);
  const [lastDisplayColumns, setLastDisplayColumns] = useState(() => getDefaultEquipmentBasedColumns());
  const localUserFilters = useRef(userFilters);
  const [defaultShowColumns, setDefaultShowColumns] = useState(() => getDefaultEquipmentBasedColumns());
  const loadFilterData = useCallback(async () => {
    if (!user?.site || !user?.id) return;

    try {
      const { masterFiltersList, myLabFilterMaster, userDetail, userDisplayColumnMaster } = await getAllMasterFilterData({
        client: client,
        site: user.site,
        userID: user.id,
        cachedUser: user
      });

      setMyLabFilter(myLabFilterMaster);
      setMasterDataFilters(() => masterFiltersList);
      setUserDisplayColumnMaster(userDisplayColumnMaster);
      setLastFavFilter(() => userDetail?.lastFavFilter);
      setLastDisplayColumns(() => userDetail?.lastDisplayColumns);
      const withFilterId = (userDetail?.userFilters || []).map((filter) => {
        return {
          ...filter,
          filterDefinition: {
            ...filter?.filterDefinition,
            filterId: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          },
        };
      });

      setUserFilters(withFilterId);

      const defaultShowCols = filter(allTableColumn(listGroupByView === 'NONE'), { show: true });
      setDefaultShowColumns(() => {
        return defaultShowCols?.map((value) => {
          return getColumnObj(value, [], convertToCameCase(autoGroupColField)); // TODO
        });
      });

    } catch (error) {
      console.error("Error loading filter data:", error);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  const { createBookingCallback } = useBookingOperations();

  const rejectBookingCallback = createBookingCallback({
    operation: BOOKING_OPERATIONS.REJECT,
    resetStates: {
      multiDeleteDialog: setOpenMultiDeleteDialog,
      status: setStatus,
    },
    clearSelections: [setTempRowData],
    onSuccess: () => {
      if (dispatchAction) {
        dispatchAction({ type: "fetching", payload: false });
        dispatchAction({ type: "clearAll" });
      }
    }
  });

  useTvMode({
    isTimelineView,
    calendarRef,
    onTick: () => setTvRefreshKey(key => key + 1),
    deps: [selectedDateRange?.[0], selectedDateRange?.[1], savedFilter, listGroupByView, signeeFilter?.userId],
  });

  // Effect to handle invalid filter display with delay
  useEffect(() => {
    if (groupName !== savedFilter && isGroupView) {
      const timeoutId = setTimeout(() => {
        setShowInvalidFilter(true);
      }, 2000); // 2 second delay

      return () => clearTimeout(timeoutId);
    } else {
      setShowInvalidFilter(false);
    }
  }, [groupName, savedFilter, isGroupView]);

  // Improved localStorage persistence with batching
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const filtersToSave = {
        [storageKeys.SAVED_FILTER]: savedFilter,
        [storageKeys.SIGNEE_FILTER]: signeeFilter,
        [storageKeys.SHOW_FILTERS]: showFilters,
        [storageKeys.SHOW_ADVANCE_FILTER]: showAdvanceFilter,
        [storageKeys.SEARCH_QUERY]: searchQuery,
        [storageKeys.SELECTED_DATE_RANGE]: selectedDateRange,
        [storageKeys.VIEW]: view,
        [storageKeys.IS_EQUIPMENT_VIEW]: isEquipmentView,
        [storageKeys.LIST_GROUP_BY_VIEW]: listGroupByView,
        [storageKeys.DISPLAY_PRESET]: displayPreset,
      };

      // Batch localStorage updates to prevent multiple DOM writes
      Object.entries(filtersToSave).forEach(([key, value]) => {
        try {
          setPersistedFilter(key, value);
        } catch (error) {
          console.warn('Failed to persist filter:', key, error);
        }
      });
    }, 100); // Small delay to batch rapid changes

    return () => clearTimeout(timeoutId); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilter, signeeFilter?.userId, showFilters, showAdvanceFilter, searchQuery, displayPreset, selectedDateRange, view, isEquipmentView, listGroupByView, isGroupView]);

  useEffect(() => {
    currentViewRef.current = view;
  }, [view]);

  useEffect(() => {
    isEquipmentViewRef.current = isEquipmentView;
  }, [isEquipmentView]);

  // Memoize the booking callback to prevent recreation
  const bookingCallback = useMemo(() => {
    return (data) => {
      const subscriptionData = data?.data?.onUpdateBookingCommand;
      const currentView = currentViewRef.current;

      if (!subscriptionData) return;

      const processingResult = subscriptionData.processingResult;
      const commandId = subscriptionData.id;

      if (currentView === 'GRID') {
        if (!calendarRef.current) {
          setTimeout(() => {
            if (calendarRef.current) {
              bookingCallback(data);
            } else {
              console.error('[Bookings] calendarRef still null after timeout');
            }
          }, 100);
          return;
        }

        let operation = BOOKING_OPERATIONS.CANCEL;
        if (processingResult === 'APPROVED_SUCCEEDED') {
          operation = BOOKING_OPERATIONS.APPROVE;
        } else if (processingResult === 'REJECTED_SUCCEEDED') {
          operation = BOOKING_OPERATIONS.REJECT;
        } else if (quickEditTracker.isQuickEdit(commandId)) {
          operation = quickEditTracker.getOperation(commandId) || BOOKING_OPERATIONS.UPDATE;
        }

        calendarRef.current.handleSubscriptionEvent(data, operation);
        return;
      }

      let operation = BOOKING_OPERATIONS.CANCEL;

      if (processingResult === 'APPROVED_SUCCEEDED') {
        operation = BOOKING_OPERATIONS.APPROVE;
      } else if (processingResult === 'REJECTED_SUCCEEDED') {
        operation = BOOKING_OPERATIONS.REJECT;
      } else if (quickEditTracker.isQuickEdit(commandId)) {
        operation = quickEditTracker.getOperation(commandId) || BOOKING_OPERATIONS.UPDATE;
      }

      const callback = createBookingCallback({
        operation: operation,
        resetStates: {
          multiDeleteDialog: setOpenMultiDeleteDialog,
          status: setStatus,
          dropBooking: setIsDropBooking,
          dialog: setIsDialogOpen,
          hazardousRejectDialog: setOpenHazardousRejectDialog,
          assistanceDialog: setOpenAssistanceDialog,
          editMode: setIsEditMode,
        },
        clearSelections: [setTempRowData],
        triggerRefresh: () => {
          triggerFetch(false);
          triggerFetch(true);
        },
        onSuccess: (data) => {
          const commandId = data?.data?.onUpdateBookingCommand?.id;
          const currentViewInCallback = currentViewRef.current;

          if (currentViewInCallback === 'GRID' && quickEditTracker.isQuickEdit(commandId)) {
            return;
          }

          setHazardousBookingToReject(null);
          setBookingForAssistance(null);
          if (dispatchAction) {
            dispatchAction({ type: "fetching", payload: false });
            dispatchAction({ type: "clearAll" });
          }
        }
      });

      callback(data);
    };
  }, [createBookingCallback, triggerFetch, dispatchAction]);

  // Handle subscription status notifications
  useBookingSubscriptionStatus(subscriptionStatus, setSubscriptionStatus);

  useEffect(() => {
    const unorderedHidenColumn = [];
    const orderedShowColumns = sortBy(
      convertStrToObjColumns(lastDisplayColumns)?.displayDefinition?.showColumns,
      ["order"],
      ["asc"]
    );

    allTableColumn(listGroupByView === 'NONE')?.forEach((value, index) => {
      const obj = find(orderedShowColumns, {
        key: value?.key,
      });
      if (!obj) {
        unorderedHidenColumn.push(value);
      }
    });

    const redefinedColumns = [...orderedShowColumns, ...unorderedHidenColumn]?.map((value) => {
      return getColumnObj(value, masterDataFilters, convertToCameCase(autoGroupColField));
    });
    const defaultShowCols = filter(allTableColumn(listGroupByView === 'NONE'), { show: true });
    setDefaultShowColumns(() => {
      return defaultShowCols?.map((value) => {
        return getColumnObj(value, masterDataFilters, convertToCameCase(autoGroupColField));
      });
    });
    setDataModel([...redefinedColumns]);
    // eslint-disable-next-line
  }, [masterDataFilters]);

  // Memoize view display functions to prevent recreation
  const listViewDisplay = useCallback(() => {
    const componentKey = generateViewKey(view, true, savedFilter, signeeFilter?.userId || 'all', listGroupByView || 'BOOKING', displayPreset?.label || 'none', currentSideBarPanel || 'none');
    const listCommonProps = {
      setEnableResetAll,
      selectedDateRange,
      setDisableResetAll,
      setSelectedDateRange,
      setSelectedEquipment,
      selectedEquipment,
      showAdvanceFilter,
      resetAll,
      isGroupView,
      setSearchQuery,
      searchQuery,
      setResetAll,
      setIsDropBooking,
      setIsDialogOpen,
      setOpenMultiDeleteDialog,
      setRowEquipmentId,
      tempRowData,
      setTempRowData,
      setPopupEnableStatus,
      rejectBookingCallback,
      setOpenHazardousRejectDialog,
      setHazardousBookingToReject,
      setOpenAssistanceDialog,
      setBookingForAssistance,
      setIsEditMode,
      openHazardousRejectDialog,
      hazardousBookingToReject,
      openAssistanceDialog,
      bookingForAssistance,
      isEditMode, listGroupByView,
      signeeFilter, setSigneeFilter, displayPreset, dataModel, setDisplayPreset, defaultShowColumns, shouldFetchData,
      userFilters, setUserFilters, lastDisplayColumns, setLastDisplayColumns, lastFavFilter, setLastFavFilter, setSavedFilter,
      userDisplayColumnMaster, setUserDisplayColumnMaster, isColumnActive, setIsColumnActive, localUserFilters, savedFilter,
      myLabFilter, setShowSideBar, showSideBar, currentSideBarPanel, setCurrentSideBarPanel, isFilterActive, setIsFilterActive
    };
    if (listGroupByView === 'NONE' || listGroupByView === 'EQUIPMENT') {
      return <EquipmentBookingList
        key={componentKey}
        {...listCommonProps}
      />
    }
    return <BookingList
      key={componentKey}
      {...listCommonProps}
    />; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupView, listGroupByView, savedFilter, signeeFilter?.userId, showFilters, tempRowData, displayPreset, showAdvanceFilter, dataModel,// eslint-disable-next-line react-hooks/exhaustive-deps
    setIsEquipmentView, setSavedFilter, setSigneeFilter, shouldFetchData, setShowFilters, selectedDateRange, selectedEquipment, searchQuery, showSideBar]);

  const calendarViewDisplay = useCallback(() => {
    const componentKey = generateViewKey(view, isEquipmentView, savedFilter, signeeFilter?.userId || 'all', listGroupByView || 'BOOKING', displayPreset?.label || 'none', currentSideBarPanel || 'none', isTimelineView);
    const calendarCommonProps = {
      eventID,
      isEquipmentView,
      setIsEquipmentView,
      setPopupEnableStatus,
      savedFilter,
      setSavedFilter,
      signeeFilter,
      setSigneeFilter,
      showFilters,
      setShowFilters,
      isGroupView,
      view,
      listGroupByView,
      setListGroupByView,
      isTimelineView,
      tvRefreshKey
    };
    if (isTimelineView) {
      return <EquipmentBookingGrid
        ref={calendarRef}
        key={`${componentKey}__tv_${tvRefreshKey}`}
        {...calendarCommonProps}
        selectedDateRange={selectedDateRange}
      />
    }
    return <BookingGrid
      ref={calendarRef}
      key={componentKey}
      {...calendarCommonProps}
    /> // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, tvRefreshKey, eventID, isEquipmentView, isTimelineView, setPopupEnableStatus, savedFilter, signeeFilter?.userId, showFilters, showAdvanceFilter, // eslint-disable-next-line react-hooks/exhaustive-deps
    setIsEquipmentView, setSavedFilter, setSigneeFilter, setShowFilters, selectedDateRange, isGroupView, listGroupByView, displayPreset?.label, currentSideBarPanel]);

  useEffect(() => {
    if (view === 'GRID') {
      setIsEquipmentView(listGroupByView === TIMELINE_VIEW)
    }
  }, [view, listGroupByView]);

  useEffect(() => {
    // Reset state when switching views to ensure clean data load
    resetViewState({
      setTempRowData,
      setSelectedEquipment
    }, dispatchAction);

    // Log view switch for debugging
    // logViewSwitch(view, isEquipmentView, savedFilter, signeeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isEquipmentView, listGroupByView, savedFilter, signeeFilter?.userId, setTempRowData, setSelectedEquipment, dispatchAction]);

  return (
    <SharedFilterProvider client={client} user={user}>
      <MainViewStyled $isTvMode={isTvMode}>
        <>
          <BookingFilter
            resetAll={resetAll}
            setEnableResetAll={setEnableResetAll}
            enableResetAll={enableResetAll}
            setResetAll={setResetAll}
            showAdvanceFilter={showAdvanceFilter}
            setShowAdvanceFilter={setShowAdvanceFilter}
            setSelectedEquipment={setSelectedEquipment}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            tempRowData={tempRowData}
            setTempRowData={setTempRowData}
            disableResetAll={disableResetAll}
            setDisableResetAll={setDisableResetAll}
            view={view}
            setView={setView}
            isEquipmentView={isEquipmentView || isTimelineView}
            isTimelineView={isTimelineView}
            isTvMode={isTvMode}
            setIsEquipmentView={setIsEquipmentView}
            listGroupByView={listGroupByView}
            setListGroupByView={setListGroupByView}
            savedFilter={savedFilter}
            setSavedFilter={setSavedFilter}
            signeeFilter={signeeFilter}
            setSigneeFilter={setSigneeFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            groupName={groupName}
            isGroupView={isGroupView}
            displayPreset={displayPreset}
            setDisplayPreset={setDisplayPreset}
            showInvalidFilter={showInvalidFilter}
            userFilters={userFilters}
            userDisplayColumnMaster={userDisplayColumnMaster}
            myLabFilter={myLabFilter}
            showSideBar={showSideBar}
            setShowSideBar={setShowSideBar}
            currentSideBarPanel={currentSideBarPanel} setCurrentSideBarPanel={setCurrentSideBarPanel}
            isFilterActive={isFilterActive} setIsFilterActive={setIsFilterActive} isColumnActive={isColumnActive} setIsColumnActive={setIsColumnActive}
          />

          {(() => {
            const shouldRenderViews = !showInvalidFilter || !isGroupView;
            return shouldRenderViews ? (
              view === LIST_VIEW ? (
                listViewDisplay()
              ) : (
                calendarViewDisplay()
              )
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "8px 16px",
                  marginTop: "20px",
                  textAlign: "center",
                  height: "100%",
                }}
              >
                <span
                  style={{
                    backgroundColor: "#fefde1",
                    border: "1px solid #ccc",
                    padding: "8px 12px",
                    borderRadius: "2px",
                    fontSize: "14px",
                  }}
                >
                  {INVALID_FILTER}
                </span>
              </div>
            );
          })()}
          <BookingNotificationStatus
            tryTimes={3}
            timeout={5 * 60 * 1000}
            onTimeoutCallback={timeoutCallbackFactory()}
            onBookingCallback={bookingCallback}
          />
          {isDropBooking && (
            <DeleteBooking
              setIsDropBooking={setIsDropBooking}
              setIsDialogOpen={setIsDialogOpen}
              isDialogOpen={isDialogOpen}
              item={rowEquipmentId}
              status={status}
              setStatus={setStatus}
              triggerFetch={triggerFetch}
            />
          )}
          {openMultiDeleteDialog && (
            <MyBookingMultiDeleteDialog
              openMultiDeleteDialog={openMultiDeleteDialog}
              setOpenMultiDeleteDialog={setOpenMultiDeleteDialog}
              selectedEquipment={selectedEquipment}
              setSelectedEquipment={setSelectedEquipment}
              status={status}
              setStatus={setStatus}
            />
          )}
        </>
      </MainViewStyled>
    </SharedFilterProvider>
  );
}

export default withApollo(Bookings);
